import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardMedia,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import {
  ArrowBack,
  LocalShipping,
  PhotoCamera,
  Delete,
  Send,
  CheckCircle,
  Warning
} from '@mui/icons-material';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';
import { brand } from '../../theme/colors';

const MerchantDeliverySubmission = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(null); // null = use order's method, 'delivery' or 'pickup' = override
  const [pickupPersonNameOverride, setPickupPersonNameOverride] = useState('');
  const [deliveryData, setDeliveryData] = useState({
    deliveryDate: new Date().toISOString().split('T')[0],
    deliveryTime: new Date().toTimeString().slice(0, 5),
    deliveryPersonName: '',
    deliveryPersonPhone: '',
    receivedByName: '',
    deliveryProofPhotos: [],
    deliveryNoteUrl: '',
    gpsCoordinates: null,
    pickupCode: '',
    pickupPersonIdNumber: '',
    pickupPersonIdVerified: false
  });

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  // Best-effort GPS capture for delivery proof — never blocks submission if
  // the browser denies permission or times out.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDeliveryData(prev => ({
          ...prev,
          gpsCoordinates: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            captured_at: new Date().toISOString()
          }
        }));
      },
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }, []);

  const loadOrder = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(`/material-orders/${orderId}`);

      if (response.data.success) {
        const orderData = response.data.data;
        // Ensure delivery_method has a default value if not set (for old orders)
        if (!orderData.delivery_method) {
          orderData.delivery_method = 'delivery'; // Default to delivery for backward compatibility
        }
        setOrder(orderData);
        // Initialize selected delivery method to order's method
        setSelectedDeliveryMethod(orderData.delivery_method || 'delivery');
      }
    } catch (err) {
      console.error('Error loading order:', err);
      setError('Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'delivery_proof');

        const response = await api.post(
          '/uploads/documents',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.success) {
          uploadedUrls.push(response.data.data.url);
        }
      }

      setDeliveryData(prev => ({
        ...prev,
        deliveryProofPhotos: [...prev.deliveryProofPhotos, ...uploadedUrls]
      }));
    } catch (err) {
      console.error('Error uploading photos:', err);
      setError('Failed to upload some photos');
    }
  };

  const removePhoto = (index) => {
    setDeliveryData(prev => ({
      ...prev,
      deliveryProofPhotos: prev.deliveryProofPhotos.filter((_, i) => i !== index)
    }));
  };

  const handleDeliveryNoteUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'delivery_note');

      const response = await api.post(
        '/uploads/documents',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        setDeliveryData(prev => ({
          ...prev,
          deliveryNoteUrl: response.data.data.url
        }));
      }
    } catch (err) {
      console.error('Error uploading delivery note:', err);
      setError('Failed to upload delivery note');
    }
  };

  const handleSubmitClick = () => {
    // Show reminder dialog first
    setShowReminderDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowReminderDialog(false);
    await submitDelivery();
  };

  const submitDelivery = async () => {
    try {
      setSubmitting(true);
      setError('');

      if (!deliveryData.deliveryDate) {
        setError('Delivery date is required');
        return;
      }

      const isPickup = selectedDeliveryMethod === 'pickup';
      
      if (!isPickup && deliveryData.deliveryProofPhotos.length === 0) {
        setError('Please upload at least one delivery proof photo');
        return;
      }

      if (isPickup) {
        if (!pickupPersonNameOverride && !order.pickup_person_name) {
          setError('Please enter the pickup person\'s name');
          return;
        }
        
        // The pickup code is verified by the backend (the merchant never
        // sees the expected value) — just require that one credential or the
        // other was collected from the person picking up.
        if (!deliveryData.pickupCode && !deliveryData.pickupPersonIdNumber) {
          setError('Please enter either the pickup code or the pickup person\'s ID number');
          return;
        }
      }

      // Prepare submission data based on selected method
      // Ensure pickupPersonIdVerified is a boolean, not an empty string
      // Also ensure all boolean fields are properly converted
      
      // The backend recomputes code-based verification itself (the merchant
      // never sees the expected code) — only pass through an explicit
      // ID-verified confirmation.
      let calculatedVerified = false;
      if (isPickup) {
        if (deliveryData.pickupPersonIdVerified === true ||
            deliveryData.pickupPersonIdVerified === 'true' ||
            deliveryData.pickupPersonIdVerified === 1) {
          calculatedVerified = true;
        }
      }
      
      const submissionData = {
        ...deliveryData,
        deliveryMethod: selectedDeliveryMethod, // Override with selected method
        pickupPersonName: isPickup ? (pickupPersonNameOverride || order.pickup_person_name) : null,
        // Always set as explicit boolean, never empty string
        pickupPersonIdVerified: calculatedVerified
      };
      
      // Clean up: remove any empty string values that should be null or boolean
      Object.keys(submissionData).forEach(key => {
        if (submissionData[key] === '') {
          // For boolean fields, set to false; for others, set to null
          if (key === 'pickupPersonIdVerified') {
            submissionData[key] = false;
          } else if (key !== 'pickupCode' && key !== 'pickupPersonIdNumber') {
            submissionData[key] = null;
          }
        }
      });

      const response = await api.post(
        `/material-orders/${orderId}/delivery`,
        submissionData
      );

      if (response.data.success) {
        // Success - navigate back or show success message
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(response.data.error || 'Failed to submit delivery');
      }
    } catch (err) {
      console.error('Error submitting delivery:', err);
      setError(err.response?.data?.error || 'Failed to submit delivery. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Order not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          {selectedDeliveryMethod === 'pickup' ? 'Mark Order as Picked Up' : 'Mark Order as Delivered'}
        </Typography>
        {selectedDeliveryMethod === 'pickup' && (
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2">
              <strong>Pickup Verification Required:</strong> The pickup person must provide the pickup code given to them by the client, or their ID number for verification.
            </Typography>
          </Alert>
        )}
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Order Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Order ID:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{order.id}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Client:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{order.client_name || 'N/A'}</Typography>
          </Grid>
          {order.delivery_method === 'delivery' && (
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Original Delivery Address:</Typography>
              <Typography variant="body1">
                {order.delivery_address?.address || order.delivery_address?.city || 'N/A'}
              </Typography>
            </Grid>
          )}
          {order.delivery_method === 'pickup' && (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Pickup Person:</Typography>
                <Typography variant="body1">
                  {order.pickup_person_name || 'N/A'}
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary">Total Amount:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              ${parseFloat(order.total_amount || 0).toFixed(2)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Delivery/Pickup Details Form */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {selectedDeliveryMethod === 'pickup' ? 'Pickup Details' : 'Delivery Details'}
          </Typography>
          {/* Allow merchant to switch method for orders originally set as delivery */}
          {order.delivery_method === 'delivery' && (
            <FormControl component="fieldset" size="small">
              <RadioGroup
                row
                value={selectedDeliveryMethod}
                onChange={(e) => {
                  setSelectedDeliveryMethod(e.target.value);
                  // Clear relevant fields when switching
                  if (e.target.value === 'pickup') {
                    setDeliveryData(prev => ({
                      ...prev,
                      deliveryProofPhotos: [],
                      deliveryPersonName: '',
                      deliveryPersonPhone: '',
                      receivedByName: ''
                    }));
                  } else {
                    setDeliveryData(prev => ({
                      ...prev,
                      pickupCode: '',
                      pickupPersonIdNumber: ''
                    }));
                  }
                }}
              >
                <FormControlLabel value="delivery" control={<Radio />} label="Delivery" />
                <FormControlLabel value="pickup" control={<Radio />} label="Pickup" />
              </RadioGroup>
            </FormControl>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label={selectedDeliveryMethod === 'pickup' ? 'Pickup Date *' : 'Delivery Date *'}
              value={deliveryData.deliveryDate}
              onChange={(e) => setDeliveryData({ ...deliveryData, deliveryDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="time"
              label={selectedDeliveryMethod === 'pickup' ? 'Pickup Time' : 'Delivery Time'}
              value={deliveryData.deliveryTime}
              onChange={(e) => setDeliveryData({ ...deliveryData, deliveryTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {selectedDeliveryMethod === 'delivery' && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Delivery Person Name"
                  value={deliveryData.deliveryPersonName}
                  onChange={(e) => setDeliveryData({ ...deliveryData, deliveryPersonName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Delivery Person Phone"
                  value={deliveryData.deliveryPersonPhone}
                  onChange={(e) => setDeliveryData({ ...deliveryData, deliveryPersonPhone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Received By (Name of person who received on site)"
                  value={deliveryData.receivedByName}
                  onChange={(e) => setDeliveryData({ ...deliveryData, receivedByName: e.target.value })}
                />
              </Grid>
            </>
          )}
          {selectedDeliveryMethod === 'pickup' && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Pickup Person Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pickup Person Name *"
                  value={pickupPersonNameOverride || order.pickup_person_name || ''}
                  onChange={(e) => setPickupPersonNameOverride(e.target.value)}
                  helperText={order.pickup_person_name ? "Name provided by client during order (you can edit if needed)" : "Enter the name of person picking up"}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Verification Method (Choose One):
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pickup Code"
                  value={deliveryData.pickupCode}
                  onChange={(e) => {
                    const code = e.target.value.toUpperCase().trim();
                    setDeliveryData({ ...deliveryData, pickupCode: code, pickupPersonIdNumber: '' });
                  }}
                  placeholder="Enter pickup code provided by the client"
                  helperText="The code is verified when you submit"
                  inputProps={{ maxLength: 6, style: { textTransform: 'uppercase' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="OR - ID Number"
                  value={deliveryData.pickupPersonIdNumber}
                  onChange={(e) => setDeliveryData({ ...deliveryData, pickupPersonIdNumber: e.target.value, pickupCode: '' })}
                  placeholder="Enter ID number"
                  helperText="If the code is not available, verify ID instead"
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Verification:</strong> The pickup person must provide either:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                    <li>The pickup code (provided by the client)</li>
                    <li>OR their ID number (must match the name: <strong>{pickupPersonNameOverride || order.pickup_person_name || 'entered above'}</strong>)</li>
                  </Box>
                </Alert>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Delivery Proof Photos (only for delivery) */}
      {selectedDeliveryMethod === 'delivery' && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Delivery Proof Photos *
          </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload photos showing the delivered materials at the delivery location:
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {deliveryData.deliveryProofPhotos.map((photoUrl, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card>
                <CardMedia
                  component="img"
                  height="200"
                  image={photoUrl}
                  alt={`Delivery proof ${index + 1}`}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                  <IconButton size="small" color="error" onClick={() => removePhoto(index)}>
                    <Delete />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: '2px dashed',
                borderColor: 'grey.300'
              }}
              onClick={() => document.getElementById('delivery-photo-upload').click()}
            >
              <Box sx={{ textAlign: 'center' }}>
                <PhotoCamera sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Add Photo
                </Typography>
              </Box>
              <input
                id="delivery-photo-upload"
                type="file"
                hidden
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
              />
            </Card>
          </Grid>
        </Grid>
      </Paper>
      )}

      {/* Delivery Note (only for delivery) */}
      {selectedDeliveryMethod === 'delivery' && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Delivery Note (Optional)
        </Typography>
        {deliveryData.deliveryNoteUrl ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Delivery note uploaded: {deliveryData.deliveryNoteUrl.split('/').pop()}
            </Alert>
            <Button
              variant="outlined"
              onClick={() => setDeliveryData({ ...deliveryData, deliveryNoteUrl: '' })}
            >
              Remove & Upload New
            </Button>
          </Box>
        ) : (
          <Button
            variant="outlined"
            startIcon={<LocalShipping />}
            component="label"
          >
            Upload Delivery Note (PDF)
            <input
              type="file"
              hidden
              accept=".pdf"
              onChange={handleDeliveryNoteUpload}
            />
          </Button>
        )}
      </Paper>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button onClick={() => navigate('/dashboard')} variant="outlined" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitClick}
          disabled={
            submitting || 
            !deliveryData.deliveryDate || 
            (selectedDeliveryMethod === 'delivery' && deliveryData.deliveryProofPhotos.length === 0) || 
            (selectedDeliveryMethod === 'pickup' && (
              (!pickupPersonNameOverride && !order.pickup_person_name) ||
              (!deliveryData.pickupCode && !deliveryData.pickupPersonIdNumber)
            ))
          }
          startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
          sx={{
            background: brand.gradient,
            '&:hover': {
              background: brand.gradientHover
            }
          }}
        >
          {submitting ? 'Submitting...' : selectedDeliveryMethod === 'pickup' ? 'Mark as Picked Up' : 'Submit Delivery Confirmation'}
        </Button>
      </Box>

      {/* Reminder Dialog */}
      <Dialog
        open={showReminderDialog}
        onClose={() => setShowReminderDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setShowReminderDialog(false)} />
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="warning" />
          <Typography variant="h6">Important Reminder</Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Please confirm:</strong> You can only mark this order as {selectedDeliveryMethod === 'pickup' ? 'picked up' : 'delivered'} if:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              {selectedDeliveryMethod === 'pickup' ? (
                <li>The order has been <strong>picked up from your store</strong> by the authorized person ({pickupPersonNameOverride || order.pickup_person_name || 'the person named above'}) and you have verified their ID or pickup code</li>
              ) : (
                <>
                  <li>The order has been <strong>picked up from your store</strong>, OR</li>
                  <li>The order has been <strong>delivered to the client's location</strong></li>
                </>
              )}
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>What happens next:</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                • The client will be notified and asked to confirm receipt<br/>
                • If the client confirms, payment will be released immediately<br/>
                • If the client does not confirm within <strong>72 business hours</strong>, payment will be automatically released to you
              </Typography>
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReminderDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            disabled={submitting}
            sx={{
              background: brand.gradient,
              '&:hover': {
                background: brand.gradientHover
              }
            }}
          >
            Yes, Mark as Delivered
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MerchantDeliverySubmission;

